import {
  OnInit,
  OnDestroy,
  inject,
  input,
  computed,
  signal,
  Directive,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { makeTranslateService } from 'src/data/services/common/translate/translate-service-factory';
import { LanguageKey } from 'src/main/translate/language-key/language-key';

@Directive()
export abstract class BaseInputComponent<T = string>
  implements ControlValueAccessor, OnInit, OnDestroy
{
  // Injections
  ngControl = inject(NgControl, { optional: true, self: true });
  protected translateService = makeTranslateService();

  // Inputs
  label = input<LanguageKey | null | undefined>();
  type = input<string>('text');
  placeholder = input<LanguageKey | null | undefined>();
  inputId = input<string>(
    `vs-input-text-${Math.random().toString(36).substring(2, 11)}`,
  );
  fullWidth = input<boolean>(true);
  maxLength = input<number | null | undefined>();
  inputMode = input<string | null | undefined>();
  numericOnly = input<boolean>(false);
  readOnly = input<boolean>(false);

  // Computeds
  placeholderValue = computed(() => {
    const pValue = this.placeholder();
    if (!pValue) return '';

    return this.translateService.translateKey(pValue);
  });

  // Signals
  value = signal<T | null | undefined>(null);
  disabled = signal<boolean>(false);
  hasErrorSignal = signal<boolean>(false);

  // Fields
  private statusSub?: Subscription;
  private onChange: (value: T | null | undefined) => void = () => {};
  private onTouched = () => {};

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (this.ngControl?.control) {
      this.statusSub = this.ngControl.control.statusChanges.subscribe(() => {
        this.updateErrorState();
      });
    }
  }

  ngOnDestroy() {
    this.statusSub?.unsubscribe();
  }

  private updateErrorState(): void {
    if (!this.ngControl) return;

    const invalid = !!this.ngControl.invalid;
    const touched = !!this.ngControl.touched;
    const dirty = !!this.ngControl.dirty;

    const shouldShowError = invalid && (touched || dirty);

    this.hasErrorSignal.set(shouldShowError);
  }

  onInputText(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const val: any = this.numericOnly()
      ? inputElement.value.replace(/\D/g, '')
      : inputElement.value;

    if (this.numericOnly() && inputElement.value !== val) {
      inputElement.value = val;
    }

    this.onInput(val);
  }

  onInput(val: T): void {
    this.value.set(val);
    this.onChange(val);

    this.updateErrorState();
  }

  onBlur(): void {
    this.onTouched();

    this.updateErrorState();
  }

  writeValue(obj: any): void {
    this.value.set(obj || '');

    this.updateErrorState();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
